<script>
import store from '@state/store'
import { format, parseISO } from 'date-fns/fp'
import Layout from '@layouts/loggedin.vue'
import { authComputed } from '@state/helpers'
import { TABLE_FIELDS } from '@kynd-points/constant'
import ActivityTableItem from '@kynd-points/components/activity-table-item.vue'
import SendPointsModal from '@kynd-points/components/send-points-modal.vue'

export default {
  components: {
    Layout,
    ActivityTableItem,
    SendPointsModal,
  },
  data() {
    return {
      tableFields: TABLE_FIELDS,
      sendPointsModalFlag: false,
    }
  },
  computed: {
    ...authComputed,
    companyId() {
      return this.$route.params.companyId
    },
    companies() {
      return this.currentUser.companies
    },
    currentCompany() {
      let currentCompany = {}
      currentCompany = this.currentUser.companies.find(
        (item) => +item.id === +this.companyId
      )
      return currentCompany || {}
    },
    funds() {
      return this.$store.state.kyndPoints.funds
    },
    incoming() {
      return this.$store.state.kyndPoints.incoming
    },
    lists() {
      return this.$store.state.kyndPoints.lists || []
    },
    hasMore() {
      return this.$store.state.kyndPoints.hasMore
    },
    loaded() {
      return this.$store.state.kyndPoints.loaded
    },
    hasNoData() {
      return !this.lists.length && this.loaded
    },
    loading() {
      return !this.loaded
    },
  },
  methods: {
    formatDate(date) {
      return format('M/D/YYYY', parseISO(date))
    },
    fetchMore() {
      if (this.loading) return
      store.dispatch('kyndPoints/fetchMore')
    },
    usePoints() {
      this.$router.push({
        name: 'companyStore',
        params: { companyId: this.companyId },
      })
    },
    showSendPointsModal() {
      this.sendPointsModalFlag = true
    },
    closeSendPointsModal() {
      this.sendPointsModalFlag = false
    },
    acceptGift(item) {
      store
        .dispatch('kyndPoints/acceptGift', { transaction_id: item.id })
        .catch(() => alert('Something went wrong.'))
    },
    refuseGift(item) {
      store
        .dispatch('kyndPoints/refuseGift', { transaction_id: item.id })
        .catch(() => alert('Something went wrong.'))
    },
  },
}
</script>

<template>
  <Layout>
    <div class="bg-white pt-4 px-8 pb-12">
      <div class="text-black text-xl">
        {{ currentCompany.name }} Marketplace
      </div>

      <div class="mt-5 text-blue-darker text-4xl text-center">
        <img
          :src="currentCompany.image_url"
          class="inline-block h-12 rounded-full mr-4"
        />
        <span>
          Kyndhub Points Available:
          <span class="text-blue text-6xl ml-2">
            {{ funds }}
          </span>
        </span>
      </div>

      <div v-if="companyId" class="text-center">
        <button
          class="button primary-button h-9.5 leading-relax text-sml rounded-small-button px-5 mt-3"
          @click="usePoints"
        >
          Use Points
        </button>
      </div>
    </div>

    <div class="flex p-4">
      <div class="w-1/5 pr-4">
        <div class="bg-white p-4 pb-6 rounded-xl shadow-lg mb-4">
          <div class="flex justify-between">
            <h3 class="text-xl text-blue-darker mb-5">Gifts</h3>
            <button
              class="button primary-button h-7.5 leading-relax text-sml rounded-small-button px-4"
              @click="showSendPointsModal"
            >
              Send Points
            </button>
          </div>
          <SendPointsModal
            :points="funds"
            :show-modal="sendPointsModalFlag"
            @close-modal="closeSendPointsModal"
          />
          <div class="border-b border-gray pb-2 mb-2">Incoming Gifts:</div>
          <div v-if="!incoming.length">
            No incoming gifts right now.
          </div>
          <div
            v-for="gift in incoming"
            :key="gift.id"
            class="flex justify-between items-center h-8 even:bg-gray-lighter"
          >
            <span>sent you a gift</span>
            <span class="text-xl text-blue-darker">{{
              gift.beautiful_value
            }}</span>
            <div class="flex">
              <div
                class="w-5 h-5 border border-blue-default rounded-full mr-1 cursor-pointer"
                @click="() => acceptGift(gift)"
              >
                <svg
                  viewBox="0 0 16 16"
                  fill="#069BD7"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.707 7.293a1 1 0 0 0-1.414 1.414l2 2a1 1 0 0 0 1.414 0l4-4a1 1 0 0 0-1.414-1.414L7 8.586 5.707 7.293z"
                  />
                </svg>
              </div>
              <div
                class="border border-gray-dark rounded-full text-gray h-5 w-5 text-center leading-none cursor-pointer"
                @click="() => refuseGift(gift)"
              >
                x
              </div>
            </div>
          </div>
        </div>
        <div class="bg-white p-4 pb-6 rounded-xl shadow-lg">
          <h3 class="text-xl text-blue-darker mb-4">Open Marketplaces</h3>
          <div class="text-blue leading-relaxed">
            <div v-for="company in companies" :key="company.id">
              <router-link
                :to="{
                  name: 'companyStore',
                  params: { companyId: company.id },
                }"
              >
                {{ company.name }} Marketplace
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <div class="w-4/5 bg-white p-4 pb-6 rounded-xl shadow-lg">
        <h3 class="text-xl text-blue-darker mb-5">Recent Activity</h3>

        <BaseTableHeader :header-data="tableFields" />
        <div class="table w-full">
          <div v-if="hasNoData" class="w-full text-center py-24">
            No data.
          </div>
          <ActivityTableItem
            v-for="item in lists"
            :key="item.id"
            :activity-item="item"
          />
        </div>
        <div class="text-center mt-6">
          <button
            v-if="hasMore"
            :class="[
              'button primary-button h-7.5 leading-relax text-sml rounded-small-button px-4',
              loading && 'opacity-50',
            ]"
            @click="fetchMore"
          >
            {{ loading ? 'Loading...' : 'View More' }}
          </button>
        </div>
      </div>
    </div>
  </Layout>
</template>
